import React, { useEffect, useState } from "react";
import {
    Grid,
    GridRow,
    GridColumn,
    SearchBox,
    DropdownMultiselect,
    AutocompleteDropdown,
    Stack,
    ClearFiltersButton,
    DatePicker,
    ListTable
} from "sprint-common-web";

import { useOrdersModuleApi } from "../../../orders/common/useOrdersModuleApi";
import { createLookupOrdersListColumns } from "./createLookupOrdersListColumns";
import { LookupOrderItem, mapToLookupOrderItem } from "./LookupOrderItem";
import { useOrderLookupFilters } from "./useOrderLookupFilters";

interface LookupOrdersListProps {
    onClick: (item: LookupOrderItem) => void;
}

export const LookupOrdersList = ({ onClick }: LookupOrdersListProps): JSX.Element => {
    const { getOrderRequestList } = useOrdersModuleApi();
    const {
        filters,
        statusOptions,
        setPage,
        setSort,
        setStatuses,
        setPONumber,
        setContent,
        setDistributor,
        setCreatedDateFrom,
        setCreatedDateTo,
        clearFilters,
        filterDistributorOptions
    } = useOrderLookupFilters();
    const [items, setItems] = useState<LookupOrderItem[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [isLoadingList, setIsLoadingList] = useState(false);

    useEffect(() => {
        setIsLoadingList(true);
        getOrderRequestList({
            ...filters,
            createdDateFrom: filters.createdDateFrom?.toISOString(),
            createdDateTo: filters.createdDateTo?.toISOString()
        })
            .then((res) => {
                setItems(res.items.map(mapToLookupOrderItem));
                setTotalItems(res.totalCount);
            })
            .finally(() => {
                setIsLoadingList(false);
            });
    }, [filters]);

    const columns = createLookupOrdersListColumns({ onClick });

    return (
        <Grid>
            <GridRow>
                <GridColumn sm={2}>
                    <SearchBox
                        testId="orders-filters-poNumber"
                        placeholder="PO Number"
                        onChange={setPONumber}
                        value={filters.pONumber}
                    />
                </GridColumn>

                <GridColumn sm={2}>
                    <SearchBox
                        testId="orders-filters-content"
                        placeholder="Content"
                        onChange={setContent}
                        value={filters.content}
                    />
                </GridColumn>

                <GridColumn sm={2}>
                    <DropdownMultiselect
                        testId="orders-filters-status"
                        placeholder="Status"
                        selectedKeys={filters.statuses}
                        onChange={setStatuses}
                        options={statusOptions}
                    />
                </GridColumn>

                <GridColumn sm={2}>
                    <AutocompleteDropdown
                        testId="order-requests-filters-distributor"
                        placeholder="Distributor"
                        selectedKey={filters.distributorId}
                        selectedText={filters.distributorName}
                        onChange={setDistributor}
                        onSearch={filterDistributorOptions}
                    />
                </GridColumn>

                <GridColumn sm={3}>
                    <Stack horizontalAlign="end">
                        <ClearFiltersButton onClear={clearFilters} isLabeled={true} />
                    </Stack>
                </GridColumn>
            </GridRow>
            <GridRow style={{ paddingTop: 10 }}>
                <GridColumn sm={3}>
                    <DatePicker
                        testId="orders-filters-created-date-from"
                        placeholder="Created date from"
                        value={filters.createdDateFrom}
                        onDateSelect={setCreatedDateFrom}
                    />
                </GridColumn>

                <GridColumn sm={3}>
                    <DatePicker
                        testId="orders-filters-created-date-to"
                        placeholder="Created date to"
                        value={filters.createdDateTo}
                        onDateSelect={setCreatedDateTo}
                    />
                </GridColumn>
            </GridRow>

            <GridRow>
                <ListTable
                    columns={columns}
                    items={items}
                    isLoading={isLoadingList}
                    shimmerLines={3}
                    pagination={{
                        page: filters.page,
                        pageSize: filters.pageSize,
                        totalItems,
                        onPageChange: setPage
                    }}
                    sort={{ by: filters.sortBy, order: filters.sortOrder }}
                    onSortChange={setSort}
                />
            </GridRow>
        </Grid>
    );
};
